// The guided tour of the PLOW page, for people arriving from the
// "AV PLOW & WIP" spreadsheet.
//
// The page holds several boards (Infrastructure, PI-TI, Service…), each a
// week grid of people against days, so the tour walks the parts in the order
// somebody meets them: pick a board, pick a week, read the grid, then the
// editor-only machinery (assigning cells, the job library, the board's
// settings). Editor-only steps are gated with `when` on the same capability
// the page uses to render those controls, so a viewer's tour never spotlights
// a button they do not have.
//
// Steps point at the real screen via [data-tour] attributes on PlowPage. The
// engine (shared/FeatureTour.jsx) and the show-once record
// (hooks/useFeatureTour.js) are generic; only this file knows about PLOWs.

export const PLOW_TOUR_ID = 'plow';

export const plowTourSteps = [
  {
    title: 'Welcome to the PLOW',
    body: [
      'The Plan of the Week now lives here rather than in the spreadsheet. Each board is a week at a glance: the people on it across the grid, their assignment for each day, and the week\'s deadlines alongside.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again.',
    ],
  },
  {
    title: 'One page, several boards',
    target: '[data-tour="plow-boards"]',
    body: [
      'Each team\'s PLOW is its own board with its own people, its own jobs and its own week shape: whole days, AM and PM, or AM, PM and a shift time.',
      'The page opens on the board most relevant to you: the one you are on, or the one you last looked at. Boards you have been given to view or edit are a click away.',
      'A board with a padlock is one you are not on. Click it to request access: say briefly why you need it, and its editors are told and can let you see the plan as a viewer. Editors see any open requests above their grid.',
    ],
  },
  {
    title: 'Moving between weeks',
    target: '[data-tour="plow-week-nav"]',
    body: [
      'The arrows step a week at a time and Today brings you back. Future weeks are planned here, and past weeks are the archive: no more copying finished weeks onto another sheet.',
      'Some boards are set so that only their editors see beyond the current week. On those, the next-week arrow stops at this week until Friday, when next week opens.',
    ],
  },
  {
    title: 'Just your month',
    target: '[data-tour="plow-view"]',
    body: [
      'My month shows only your own row on this board, a line per week, so you can see what you are doing for the month without everybody else\'s plan around it. The arrows then step a month at a time.',
      'Click any day or week to open that week on the full board. Week brings the whole board back.',
      'On a board where only editors see beyond the current week, My month is for its editors only.',
    ],
  },
  {
    title: 'The week itself',
    target: '[data-tour="plow-grid"]',
    body: [
      'One row per person, one column per day. A cell shows the job or status for each part of the day: a coloured chip for work, amber for leave, grey for off. When the whole day is on one job it is shown once, and the box around it is the traffic light for the day; a day split across different jobs gets a box per part.',
      'A row can also be a label rather than a person, for work that belongs to nobody in particular: a SERVICING lane, say. Its day is a list rather than AM and PM: click it and add as many tasks as there are, each a job or a note with its own traffic light, in the order they will be done. It carries no leave, shift time or standby, because there is nobody behind it.',
      'Bank holidays, company closures and approved leave arrive on their own from the rest of ops, so nobody types them in. Sickness shows in red, and a job that was already assigned that day stays visible underneath, struck through, so it can be reassigned rather than lost.',
      'A job that carries an AV reference is linked to the real SOW, which is what will feed time logging later.',
    ],
  },
  {
    title: 'A cell in full',
    target: '[data-tour="plow-grid"]',
    when: (c) => !c.canEdit,
    body: [
      'Tap any cell to open it in full: every part of the day with its job and SOW reference, the note, the traffic light, the shift time and any standby, written out rather than squeezed into the grid. Handy on a phone, where the chips are cut short.',
    ],
  },
  {
    title: 'Assigning somebody',
    target: '[data-tour="plow-grid"]',
    when: (c) => c.canEdit,
    body: [
      'Click any cell to set it: pick a job or a status for each part of the day, add a note, and tick the other days of the week to fill several at once.',
      'Working in pairs? Copy from takes another person\'s same day as the starting point: every section is filled in from them, ready to adjust and save.',
      'Copy to goes the other way: tick everybody else who is on it and they all get this day when you save. Combine it with the day ticks to plan a team across a week in one go.',
      'The red, amber and green dots traffic-light a booking: tentative, at risk, or confirmed. On the grid the colour becomes the border around the job, or around the whole day when both halves are on the same job (the most urgent colour wins).',
      'If the job is not in the library yet, + New job beside the selector creates it there and then, and picks it for that part of the day.',
      'Picking the empty entry clears a cell.',
    ],
  },
  {
    title: 'Last week, carried forward',
    target: '[data-tour="plow-copy-forward"]',
    when: (c) => c.canEdit,
    body: [
      'Copy last week brings the previous week\'s grid into this one. It only fills cells that are still empty, so anything already planned this week stays put.',
    ],
  },
  {
    title: 'Clearing days in bulk',
    target: '[data-tour="plow-clear"]',
    when: (c) => c.canEdit,
    body: [
      'Clear empties several days at once, for as many people as you like: handy after carrying last week forward. Tick days one by one, a day\'s heading for the whole board, or a name for that person\'s week.',
      'A cleared day loses everything in it. Any standby on it is cancelled, and so is a desk move or working away allowance its jobs raised.',
    ],
  },
  {
    title: 'The job library',
    target: '[data-tour="plow-jobs"]',
    when: (c) => c.canEdit,
    body: [
      'Jobs are reusable: set one up once ("AH Techbar", "Inventory Management") and assign it all year. A job can link to a SOW by its AV reference, and it gets a colour so the grid reads at a glance.',
      'Statuses like Free, Holiday and Off are built in, because the utilisation numbers count them.',
      'Two flags feed overtime. "Desk move or install" raises the desk move allowance for the day for whoever is assigned to the job, and "Working away" pays a quarter of the hourly rate on their shift that day. Both are approved from the board, so nobody has to claim them. A working away day also frees the reserved desk of whoever is on it for others to book, from the Monday of that week.',
      'When a job is finished, Archive it: it leaves the selector so nobody books against it, while every past week keeps it and its SOW. Restore brings it back.',
    ],
  },
  {
    title: 'Expanded or compact',
    target: '[data-tour="plow-expanded"]',
    when: (c) => c.canEdit,
    body: [
      'Expanded view puts a + on every empty slot, so you can see at a glance where somebody is still free to book. Untick it for a compact grid: empty slots take no room, and more of the board fits on one screen. The choice is remembered on this browser.',
    ],
  },
  {
    title: 'The weekly report for the client',
    target: '[data-tour="plow-export"]',
    when: (c) => c.canEdit,
    body: [
      'Export report builds the plan-of-works sheet some clients ask for each week. It lists this week\'s assignments grouped by AV reference; untick a job, or one person\'s day, and download the spreadsheet in the client\'s own layout.',
      'Only jobs linked to a SOW appear, because the AV reference is the row. Anything assigned without one is named so you can link it in the job library first.',
    ],
  },
  {
    title: 'A week of standby in one go',
    target: '[data-tour="plow-standby-week"]',
    when: (c) => c.canEdit && c.hasStandby,
    body: [
      'Standby for the week puts one person on a standby level for every day of this week in one save, instead of a cell at a time. Untick any day they are not covering, add a note if you like, and the days appear on the Standby row as pending.',
      'The dialog shows the hours and pay the week adds up to, and warns before you save if that person is already on standby in another row on any of those days.',
    ],
  },
  {
    title: 'Standby, months ahead',
    target: '[data-tour="plow-standby-calendar"]',
    when: (c) => c.canEdit && c.hasStandby,
    body: [
      'Standby calendar shows a month of the Standby row at a time. Pick a person and a level once, then drag across days to put them on standby; every stroke saves as you go, and the days appear on the row as pending.',
      'Switch the brush to Clear and drag to take somebody off. A stroke over a day they already hold in another standby row is named rather than saved, so nothing lands half done.',
    ],
  },
  {
    title: 'Who is on standby',
    target: '[data-tour="plow-standby-calendar"]',
    when: (c) => !c.canEdit && c.hasStandby,
    body: [
      'Standby calendar shows a month of the Standby row at a time, so you can see who is covering when without paging through the weeks. Your own days are in bold; dashed days are still pending and solid ones are confirmed.',
      'It is read only: the board\'s editors plan standby.',
    ],
  },
  {
    title: 'The standby rota, as a file',
    target: '[data-tour="plow-standby-export"]',
    when: (c) => c.canEdit && c.hasStandby,
    body: [
      'Export standby rota replaces the 24-7 Rota spreadsheet. Pick a date range and download one file that opens anywhere: today\'s cover at the top with tap-to-call numbers, then every week of the range, and a click on any name for their contact details.',
      'Numbers come from People (work phone and personal mobile), so the export is for editors. The dialog names any day with nobody at a level before you download.',
      'Choose On-call upload instead for a spreadsheet in the columns the incident system\'s Upload on-call rota screen takes. Pick which of the board\'s levels goes in its Level 1 and Level 2 columns.',
    ],
  },
  {
    title: 'The board\'s setup',
    target: '[data-tour="plow-setup"]',
    when: (c) => c.canEdit,
    body: [
      'Settings is where the board itself is shaped: its rows and their order, who may edit or view it, which sections each day splits into, and whether weekends show. A row is a person, or a label for work that belongs to nobody. A person can be blocked on set weekdays, such as two days a week on another team\'s work, and the grid shows those days as Blocked every week.',
      'It also holds "Only editors see beyond the current week": with that on, people on the board and viewers see this week and the archive, and next week opens to them on Friday.',
      'If the board has a Standby row, Settings also lists its standby levels: the company ones from Admin, and any the board adds for itself, such as SMS or ODAS, so one Standby row carries every type of standby.',
    ],
  },
  {
    title: 'Deadlines and notes',
    target: '[data-tour="plow-panels"]',
    body: [
      'The side panel carries what the spreadsheet kept in its margin columns: the week\'s deadlines, notes, and resource requests. They belong to the week you are looking at.',
    ],
  },
  {
    title: 'That\'s everything',
    body: [
      'Pick your board, check your week. If you want this walkthrough again, the "?" beside the PLOW heading replays it any time.',
    ],
  },
];
